import ApiClientOpen from '../api/axios/apiClientOpen';
import type { MarcaListResponse, MarcaResponse, MarcaRequest } from '../../types';
import qs from 'qs';
import type { StrapiFilters, StrapiSort, StrapiPopulate, StrapiPagination } from '../../types/_strapi.model';

const MARCA_ENDPOINT = '/marcas';

interface GetAllMarcasParams {
  filters?: StrapiFilters;
  sort?: StrapiSort | string[];
  fields?: string[];
  populate?: StrapiPopulate | string[];
  pagination?: StrapiPagination;
}

interface GetAllParams {
  page: number;
  limit: number;
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Service class for Marca entity operations
 */
class MarcaService {
  /**
   * Get all marcas with optional filtering, sorting, population, and pagination
   * @param params Object containing filters, sort, fields, populate, and pagination options
   * @returns Promise with list of marcas and metadata
   */
  async getAllMarcas(params?: GetAllMarcasParams): Promise<MarcaListResponse> {
    try {
      // Build query parameters using qs
      const queryParams = qs.stringify(params || {}, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<MarcaListResponse>(`${MARCA_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching marcas:', error);
      throw error;
    }
  }

  /** 
   * Get all marcas with pagination, filtering, and sorting
   * @param params Object containing pagination, filtering, and sorting options
   * @returns Promise with list of marcas and metadata
   */
  async getAll(params: GetAllParams): Promise<MarcaListResponse> {
    try {
      const { page, limit, filter, sortBy, sortOrder } = params;
      
      // Build Strapi query parameters
      const strapiParams: any = {
        pagination: {
          page,
          pageSize: limit
        }
      };
      
      // Add filter if provided
      if (filter) {
        strapiParams.filters = {
          $or: [
            { nombre: { $contains: filter } },
            { descripcion: { $contains: filter } }
          ]
        }
      }
      
      // Add sorting if provided
      if (sortBy && sortOrder) {
        strapiParams.sort = `${sortBy}:${sortOrder}`;
      }
      
      // Build query parameters using qs
      const queryParams = qs.stringify(strapiParams, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<MarcaListResponse>(`${MARCA_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching marcas:', error);
      throw error;
    }
  }

  /**
   * Get a marca by ID with optional population and field selection
   * @param id Marca ID
   * @param populate Optional fields to populate
   * @param fields Optional fields to select
   * @returns Promise with marca data
   */
  async getMarcaById(
    id: number | string, 
    populate?: StrapiPopulate | string[], 
    fields?: string[]
  ): Promise<MarcaResponse> {
    try {
      const queryOptions: any = {};
      
      if (populate) {
        queryOptions.populate = populate;
      }
      
      if (fields) {
        queryOptions.fields = fields;
      }
      
      const queryParams = qs.stringify(queryOptions, {
        encodeValuesOnly: true,
      });
      
      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<MarcaResponse>(`${MARCA_ENDPOINT}/${id}${queryString}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching marca with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new marca
   * @param marcaData Marca data to create
   * @returns Promise with created marca data
   */
  async createMarca(marcaData: MarcaRequest): Promise<MarcaResponse> {
    try {
      const response = await ApiClientOpen.post<MarcaResponse>(MARCA_ENDPOINT, marcaData);
      return response.data;
    } catch (error) {
      console.error('Error creating marca:', error);
      throw error;
    }
  }

  /**
   * Create a new marca (alias method)
   * @param marcaData Marca data to create
   * @returns Promise with created marca data
   */
  async create(marcaData: MarcaRequest): Promise<MarcaResponse> {
    return this.createMarca(marcaData);
  }

  /**
   * Update an existing marca
   * @param id Marca ID to update
   * @param marcaData Updated marca data
   * @returns Promise with updated marca data
   */
  async updateMarca(id: number | string, marcaData: MarcaRequest): Promise<MarcaResponse> {
    try {
      const response = await ApiClientOpen.put<MarcaResponse>(`${MARCA_ENDPOINT}/${id}`, marcaData);
      return response.data;
    } catch (error) {
      console.error(`Error updating marca with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing marca (alias method)
   * @param id Marca ID to update
   * @param marcaData Updated marca data
   * @returns Promise with updated marca data
   */
  async update(id: number | string, marcaData: MarcaRequest): Promise<MarcaResponse> {
    return this.updateMarca(id, marcaData);
  }

  /**
   * Delete a marca by ID (alias method)
   * @param id Marca ID to delete
   * @returns Promise indicating success or failure
   */
  async delete(id: number | string): Promise<void> {
    return this.deleteMarca(id);
  }

  /**
   * Delete a marca by ID
   * @param id Marca ID to delete
   * @returns Promise indicating success or failure
   */
  async deleteMarca(id: number | string): Promise<void> {
    try {
      await ApiClientOpen.delete(`${MARCA_ENDPOINT}/${id}`);
    } catch (error) {
      console.error(`Error deleting marca with id ${id}:`, error);
      throw error;
    }
  }
}

export default new MarcaService();